import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  ArrowLeft, MessageSquare, Info, Tag, Star, Clock, 
  Calendar, User, AlertCircle, Check, X, ChevronDown, 
  ChevronRight, Edit, History, Paperclip, Send,
  FolderOpen, FileText, Cpu , ImageIcon, ImageOff, ExternalLink // Ajoutez ces icônes
} from 'lucide-react';
import api from '../../api';

// Composants de base réutilisables
const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ title, icon: Icon, collapsible = false, onToggle, isOpen = true }) => {
  return (
    <div className="border-b border-gray-200">
      <div 
        className={`flex items-center justify-between p-3 bg-gray-50 ${collapsible ? 'cursor-pointer' : ''}`}
        onClick={collapsible ? onToggle : undefined}
      >
        <div className="flex items-center">
          {Icon && <Icon size={16} className="text-gray-500 mr-2" />}
          <h3 className="text-sm font-medium text-gray-700">{title}</h3>
        </div>
        {collapsible && (
          isOpen ? <ChevronDown size={16} className="text-gray-500" /> : <ChevronRight size={16} className="text-gray-500" />
        )}
      </div>
    </div>
  );
};

const CardBody = ({ children, className = "" }) => (
  <div className={`p-4 ${className}`}>
    {children}
  </div>
);

const FormField = ({ 
  label, 
  name, 
  value, 
  type = 'text', 
  icon: Icon, 
  onChange, 
  options = [], 
  multiple = false, 
  disabled = false, 
  error,
  className = '',
  required = false 
}) => {
  const inputClasses = `
    w-full px-3 py-2 
    border ${error ? 'border-red-300' : 'border-gray-300'} 
    rounded text-sm text-gray-700
    focus:outline-none focus:ring-1 focus:ring-terrecuite-500 focus:border-terrecuite-500
    ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}
  `;

  return (
    <div className={`mb-4 ${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        <div className="flex items-center">
          {Icon && <Icon size={14} className="text-gray-400 mr-1.5" />}
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </div>
      </label>
      
      <div className="relative">
        {type === 'textarea' ? (
          <textarea
            name={name}
            value={value || ''}
            onChange={onChange}
            disabled={disabled}
            rows={3}
            className={inputClasses}
          />
        ) : type === 'select' ? (
          <div className="relative">
            <select
              name={name}
              multiple={multiple}
              value={value || (multiple ? [] : '')}
              onChange={onChange}
              disabled={disabled}
              className={inputClasses}
            >
              {!multiple && <option value="">--</option>}
              {options.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <ChevronDown size={16} className="text-gray-400" />
            </div>
          </div>
        ) : type === 'date' ? (
          <input
            type="date"
            name={name}
            value={value || ''}
            onChange={onChange}
            disabled={disabled}
            className={inputClasses}
          />
        ) : (
          <div className="relative">
            <input
              type={type}
              name={name}
              value={value || ''}
              onChange={onChange}
              disabled={disabled}
              className={inputClasses}
            />
            {!disabled && (
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400">
                <Edit size={14} />
              </div>
            )}
          </div>
        )}
        
        {error && (
          <p className="mt-1 text-xs text-red-500 flex items-center">
            <AlertCircle size={12} className="mr-1" />
            {error}
          </p>
        )}
      </div>
    </div>
  );
};

const Badge = ({ children, variant = "En attente", onClick }) => {
  const variants = {
    
    "En attente": "bg-terrecuite-100 text-terrecuite-800",
    "en cours": "bg-yellow-100 text-yellow-800",
    "terminé": "bg-green-100 text-green-800"
  };
  
  const classes = variants[variant] || variants.default;
  
  return (
    <span 
      onClick={onClick}
      className={`px-2 py-1 text-xs font-medium rounded-full ${classes} ${onClick ? 'cursor-pointer' : ''}`}
    >
      {children}
    </span>
  );
};

const Button = ({ children, variant = "default", size = "md", icon: Icon, onClick, disabled = false }) => {
  const variants = {
    default: "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50",
    primary: "bg-terrecuite-600 text-white hover:bg-terrecuite-700",
    success: "bg-green-600 text-white hover:bg-green-700",
    danger: "bg-red-600 text-white hover:bg-red-700"
  };
  
  const sizes = {
    sm: "px-2 py-1 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-5 py-2 text-base"
  };
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        ${variants[variant]} 
        ${sizes[size]} 
        rounded font-medium inline-flex items-center justify-center
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-terrecuite-500
        ${disabled ? 'opacity-60 cursor-not-allowed' : ''}
      `}
    >
      {Icon && <Icon size={size === "sm" ? 14 : size === "md" ? 16 : 18} className="mr-2" />}
      {children}
    </button>
  );
};

const StepIndicator = ({ etapes = [], currentStep }) => {
  return (
    <div className="mb-10">
      <div className="flex items-center">
        {etapes.map((etape, index) => {
          const isActive = etape.value === currentStep;
          const isPast = etapes.findIndex(e => e.value === currentStep) > index;
          
          return (
            <div key={etape.value} className="flex items-center flex-1">
              <div className="relative flex flex-col items-center">
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center 
                  ${isActive ? 'bg-terrecuite-600 text-white' : isPast ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'}
                  ${index < etapes.length - 1 ? 'mb-2' : ''}
                `}>
                  {isPast ? <Check size={16} /> : isActive ? index + 1 : index + 1}
                </div>
                <span className={`
                  text-xs font-medium absolute -bottom-6 w-32 text-center
                  ${isActive ? 'text-terrecuite-600' : isPast ? 'text-green-600' : 'text-gray-500'}
                `}>
                  {etape.label}
                </span>
              </div>
              
              {index < etapes.length - 1 && (
                <div className={`
                  flex-1 h-1 mx-2
                  ${isPast ? 'bg-green-500' : isActive ? 'bg-terrecuite-200' : 'bg-gray-200'}
                `} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ActivityItem = ({ activity }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className="mb-4 border-b border-gray-100 pb-3">
      <div className="flex items-start">
        <div className="bg-gray-100 rounded-full w-8 h-8 flex items-center justify-center mr-3 mt-1">
          <User size={14} className="text-gray-500" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-sm font-medium text-gray-700">{activity.user}</h4>
            <span className="text-xs text-gray-500">{formatDate(activity.timestamp)}</span>
          </div>
          <p className="text-xs text-gray-600">
            <span className="font-medium">{activity.action}</span> {activity.field}
            {activity.oldValue && activity.newValue && (
              <>: <span className="line-through text-red-500">{activity.oldValue}</span> → <span className="text-green-500">{activity.newValue}</span></>
            )}
            {!activity.oldValue && activity.newValue && (
              <>: <span className="text-green-500">{activity.newValue}</span></>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default function TicketDetailCard() {
  const { id } = useParams();
  const navigate = useNavigate();
  

  // États
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState('');
  const [backendErrors, setBackendErrors] = useState({});
  const [openSections, setOpenSections] = useState({
    description: true,
    details: true,
    dates: true,
    contacts: true,
    demande: true,
    activity: true,
    bugImage: true // Nouvelle section
  });
  
  const [form, setForm] = useState({
    nom: '',
    content: '',
    type: '',
    date_debut_technique: '',
    organisation_id: '',
    deadline_client: '',
    deadline_tech: '',
    priorite: '',
    etape_id: '',
    status: '',
    createur_id: '',
    testeur_id: '',
    affectee_par: '',
    developpeur_ids: [],
    nbr_jrs: '',
    nbr_jrs_valider: '',
    chef_de_projet_id: '',
    last_update: '',
    created_at: '',
    updated_at: '',
  });
  
  const [etapes, setEtapes] = useState([]);
  const [devs, setDevs] = useState([]);
  const [activities, setActivities] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [documents, setDocuments]=useState([]);
  const [utilisateurId, setUtilisateurId]=useState(null);


    useEffect(() => {
    const fetchUser = async () => {
      try {
        const userResponse = await api.get('/me', { withCredentials: true });
        if (!userResponse.data.success) {
          console.error('Impossible de récupérer les informations utilisateur.');
          return;
        }

        const user = userResponse.data.user;
        setUtilisateurId(parseInt(user.id, 10));
      } catch (error) {
        console.error('Erreur lors de la récupération des informations utilisateur.');
      }
    };

    fetchUser();
  }, []);
  
  // Options pour les selects
  const typeOptions = [
    { value: 'demande', label: 'Demande' },
    { value: 'bug', label: 'Bug' },
  ];

  const priorityOptions = [
    { value: '1', label: 'Faible' },
    { value: '2', label: 'Moyenne' },
    { value: '3', label: 'Haute' },
  ];
  
  const statuses = ['En attente', 'en cours', 'terminé'];

  // Simuler des données d'activité
  const mockActivities = [
    {
      id: 1,
      user: 'Admin System',
      action: 'modified',
      field: 'Statut',
      oldValue: 'En attente',
      newValue: 'En cours',
      timestamp: '2023-05-15T10:30:00Z'
    },
    {
      id: 2,
      user: 'Jean Dupont',
      action: 'modified',
      field: 'Priorité',
      oldValue: '2',
      newValue: '3',
      timestamp: '2023-05-15T11:45:00Z'
    },
  ];

  // Charger les données
  useEffect(() => {
    Promise.all([
      api.get(`/tickets/${id}`),
      api.get('/parametrages/etapes'),
      api.get('/utilisateurs/developpeurs'),
    ])
      .then(([tRes, eRes, dRes]) => {
        const t = tRes.data.ticket;
        setDocuments(tRes.data.ticket.documents || []);
        setForm({
          nom: t.nom || '',
          content: t.content || '',
          type: t.type || '',
          date_debut_technique: t.date_debut_technique || '',
          organisation_id: t.organisation?.nomOrganisation || '',
          deadline_client: t.deadline_client || '',
          deadline_tech: t.deadline_tech || '',
          priorite: t.priorite?.toString() || '',
          etape_id: t.etape_id?.toString() || '',
          status: t.status || '',
          createur_id: t.createur?.nom || '',
          testeur_id: t.testeur?.nom || '',
          affectee_par: t.affectee_par?.nom || '',
          developpeur_ids: t.developpeurs?.map(d => d.utilisateur_id.toString()) || [],
          chef_de_projet_id: t.demande?.chef_de_projet_id?.toString() || '',
          nbr_jrs: t.demande?.nbr_jrs?.toString() || '',
          bug_image_url: t.bug?.image || '',
          nbr_jrs_valider: t.demande?.nbr_jrs_valider?.toString() || '',
          last_update: t.last_update || '',
          created_at: t.created_at ? t.created_at.slice(0, 10) : '',
          updated_at: t.updated_at ? t.updated_at.slice(0, 10) : '',
        });
        setEtapes(
          eRes.data.etapes.map(e => ({ value: e.id.toString(), label: e.nom }))
        );
        setDevs(
          dRes.data.developpeurs.map(d => ({
            value: d.utilisateur_id.toString(),
            label: `${d.prenom} ${d.nom}`,
          }))
        );
        setActivities(mockActivities);
      })
      .catch((e) => setErrors(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  // Handlers
  const handleChange = e => {
    const { name, value, selectedOptions } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: selectedOptions ? Array.from(selectedOptions, o => o.value) : value,
    }));
  };

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  function getNextStatus(current) {
    const idx = statuses.indexOf(current);
    return statuses[(idx + 1) % statuses.length];
  }
  
  // function updateStatus() {
  //   const next = getNextStatus(form.status);
  //   setForm(prev => ({ ...prev, status: next }));
  // }

  const handleSave = async () => {
    setBackendErrors({});
    const payload = {};

    // Champs simples (string / date)
    ['nom', 'content', 'type', 'date_debut_technique', 'deadline_client', 'deadline_tech', 'status']
      .forEach(key => {
        if (form[key]) payload[key] = form[key];
      });

    // Clés numériques (IDs, priorités...)
    [
      ['priorite', 'priorite'],
      ['etape_id', 'etape_id'],
    ].forEach(([formKey, apiKey]) => {
      if (form[formKey] !== '') {
        payload[apiKey] = parseInt(form[formKey], 10);
      }
    });

    // Tableau des développeurs
    if (form.developpeur_ids.length) {
      payload.developpeur_ids = form.developpeur_ids.map(v => parseInt(v, 10));
    }

    // Si c'est une "demande", on ajoute les champs correspondants
    if (form.type === 'demande') {
      if (form.nbr_jrs) payload.nbr_jrs = parseInt(form.nbr_jrs, 10);
      if (form.nbr_jrs_valider) payload.nbr_jrs_valider = parseInt(form.nbr_jrs_valider, 10);
      if (form.chef_de_projet_id) payload.chef_de_projet_id = parseInt(form.chef_de_projet_id, 10);
    }

    payload.modifier = utilisateurId;

    try {
      await api.post(`/tickets/${id}`, payload);
    
        toast.success('Mis à jour avec succès', {
          position: 'top-right',
          autoClose: 500,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          progress: undefined
        });
      navigate(-1);
    } catch (err) {
      if (err.response?.status === 422) {
        setBackendErrors(err.response.data.errors || {});
      } else {
        setErrors('Erreur interne du serveur');
      }
    }
  };

  const handleSendMessage = () => {
    if (newMessage.trim() === '') return;
    
    const messageActivity = {
      id: activities.length + 1,
      user: 'Utilisateur Actuel',
      action: 'added',
      field: 'Commentaire',
      newValue: newMessage,
      timestamp: new Date().toISOString()
    };
    
    setActivities(prev => [messageActivity, ...prev]);
    setNewMessage('');
  };
  
const uploadToLaravel = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  const res = await api.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return res.data.url; // URL publique renvoyée par Laravel
};
const handleUploadDocuments = async (e, ticketId) => {
  const files = e.target.files;
  if (!files || !files.length || !ticketId) return;


  setUploading(true);
  setUploadError(null);

  try {
    const uploadedDocuments = [];


    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const url = await uploadToLaravel(file);

      uploadedDocuments.push({
        document_name: file.name, 
        document_url: url,
        action: 1
      });
    }

    // Requête vers l’API Laravel
    await api.post(`/tickets/${ticketId}`, {
      modifier: utilisateurId ,
      documents: uploadedDocuments
    });

    toast.success("Documents envoyés avec succès.");
  } catch (error) {
    console.error("Erreur lors de l'envoi des documents :", error);
    setUploadError("Échec de l'envoi des documents.");
  } finally {
    setUploading(false);
  }
};

const statusLabels = {
  '1': { label: 'En attente', bg: 'bg-blue-100', text: 'text-blue-800' },
  '2': { label: 'En cours', bg: 'bg-amber-100', text: 'text-amber-800' },
  '3': { label: 'Prêt', bg: 'bg-green-100', text: 'text-green-800' },
};


  // Logique de rendu
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-pulse flex flex-col items-center">
          <div className="h-12 w-12 bg-terrecuite-500 rounded-full mb-3"></div>
          <div className="h-4 w-24 bg-gray-300 rounded"></div>
        </div>
      </div>
    );
  }

  if (errors) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="text-red-500 mr-3" size={20} />
          <p className="text-red-600">{errors}</p>
        </div>
      </div>
    );
  }

  // Vérifier si l'édition est autorisée
  const editableEtapeIds = ['1', '2']; // IDs pour les étapes où l'édition est permise
  const isEditable = editableEtapeIds.includes(form.etape_id);

return (
    <div className="min-h-screen bg-gray-100 py-6 px-4">
      {uploading && <p className="text-sm text-gray-500">⏳ Envoi des documents...</p>}
      {uploadError && <p className="text-sm text-red-500">{uploadError}</p>}
      <div className="max-w-6xl mx-auto">
        {/* En-tête */}
<div className="mb-6">
  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
    {/* Partie gauche */}
    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
      <Button 
        variant="default" 
        size="sm" 
        icon={ArrowLeft} 
        onClick={() => navigate(-1)}
        className="bg-terrecuite-100 hover:bg-terrecuite-200 text-terrecuite-800 self-start sm:self-auto"
      >
        <span className="hidden sm:inline">Retour</span>
      </Button>
      
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
        <h1 className="text-xl sm:text-2xl font-bold text-terrecuite-800 break-words">
          Ticket #{id}
        </h1>
        <Badge className={`${statusLabels[form.status].bg} ${statusLabels[form.status].text} font-medium w-fit`}>
          {statusLabels[form.status].label}
        </Badge>
      </div>
    </div>

    {/* Partie droite - Boutons d'action */}
    <div className="flex flex-col-reverse xs:flex-row gap-3 items-stretch xs:items-center">
      <div className="flex gap-2 justify-between xs:justify-start">
        <Button 
          variant="default" 
          onClick={() => navigate(-1)}
          className="bg-terrecuite-100 hover:bg-terrecuite-200 text-terrecuite-800 flex-1 xs:flex-none"
        >
          <span className="hidden xs:inline">Annuler</span>
          <span className="xs:hidden">X</span>
        </Button>
        
        <Button 
          variant="primary" 
          icon={Check}
          onClick={handleSave}
          className="bg-terrecuite-600 hover:bg-terrecuite-700 text-white flex-1 xs:flex-none"
        >
          <span className="hidden sm:inline">Sauvegarder</span>
          <span className="sm:hidden">✓</span>
        </Button>
      </div>
      
      {/* Séparateur visible seulement sur desktop */}
      <div className="hidden md:block h-6 w-px bg-gray-300 mx-2"></div>
      
      {/* Bouton Ajouter Document amélioré */}
      <div className="relative">
        <input 
          id="document-upload"
          type="file" 
          multiple 
          className="hidden" 
          onChange={(e) => handleUploadDocuments(e, id)}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.png"
        />
        
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => document.getElementById("document-upload").click()}
          className="w-full xs:w-auto relative flex items-center gap-1 xs:gap-2 bg-white border border-gray-200 hover:border-terrecuite-300 rounded-lg px-3 xs:px-4 py-2.5 text-sm font-medium text-terrecuite-700 hover:text-terrecuite-800 hover:bg-terrecuite-50 transition-all duration-200 shadow-xs hover:shadow-sm"
        >
          <Paperclip className="h-4 w-4 text-terrecuite-500 flex-shrink-0" />
          <span className="hidden xs:inline">Joindre</span>
          <span className="xs:hidden">Fichiers</span>
          <span className="absolute -right-2 -top-2 bg-terrecuite-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            +
          </span>
        </Button>
      </div>
    </div>
  </div>
  
  {/* Indicateur d'étapes avec défilement horizontal sur mobile */}
  <div className="overflow-x-auto pb-2">
    <div className="min-w-max">
      <StepIndicator 
        etapes={etapes}
        currentStep={form.etape_id}
        activeColor="terrecuite-600"
        isMobile={window.innerWidth < 768}
      />
    </div>
  </div>
</div>
        {/* Contenu principal */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Colonne gauche - Informations principales */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <Card className="border-terrecuite-200">
              <CardHeader 
                title="Description" 
                icon={MessageSquare} 
                collapsible 
                isOpen={openSections.description}
                onToggle={() => toggleSection('description')}
                className="bg-terrecuite-50 text-terrecuite-800"
              />
              {openSections.description && (
                <CardBody>
                  <FormField
                    label="Sujet"
                    name="nom"
                    value={form.nom}
                    onChange={handleChange}
                    error={backendErrors.nom?.[0]}
                    icon={Tag}
                    required
                    disabled={!isEditable}
                    className="border-terrecuite-300 focus:ring-terrecuite-500"
                  />
                  <FormField
                    label="Description"
                    name="content"
                    type="textarea"
                    value={form.content}
                    onChange={handleChange}
                    error={backendErrors.content?.[0]}
                    disabled={!isEditable}
                    className="border-terrecuite-300 focus:ring-terrecuite-500"
                  />
                </CardBody>
              )}
              
            </Card>
            
            {/* Détails */}
            <Card className="border-terrecuite-200">
              <CardHeader 
                title="Détails" 
                icon={Info} 
                collapsible 
                isOpen={openSections.details}
                onToggle={() => toggleSection('details')}
                className="bg-terrecuite-50 text-terrecuite-800"
              />
              {openSections.details && (
                <CardBody>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      label="Type"
                      name="type"
                      type="select"
                      options={typeOptions}
                      value={form.type}
                      onChange={handleChange}
                      error={backendErrors.type?.[0]}
                      icon={Tag}
                      required
                      disabled={true}
                      className="border-terrecuite-300 focus:ring-terrecuite-500"
                    />

                    <FormField
                      label="Étape"
                      name="etape_id"
                      type="select"
                      options={etapes}
                      value={form.etape_id}
                      onChange={handleChange}
                      error={backendErrors.etape_id?.[0]}
                      icon={Clock}
                      disabled={true}
                      className="border-terrecuite-300 focus:ring-terrecuite-500"
                    />
                  </div>
                </CardBody>
              )}
            </Card>
            <Card className="border-terrecuite-200">
            <CardHeader 
              title="Documents joints" 
              icon={Paperclip} 
              collapsible 
              isOpen={openSections.documents}
              onToggle={() => toggleSection('documents')}
              className="bg-terrecuite-50 text-terrecuite-800"
            />
            {openSections.documents && (
              <CardBody>
                {documents.length === 0 ? (
                  <div className="text-center py-4">
                    <FolderOpen size={24} className="mx-auto text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">Aucun document joint</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {documents.map((doc) => (
                      <div 
                        key={doc.id} 
                        className="border border-terrecuite-100 rounded-lg p-3 hover:border-terrecuite-300 transition-colors bg-white group"
                      >
                        <div className="flex items-start">
                          <div className="bg-terrecuite-50 p-2 rounded-lg mr-3">
                            <FileText size={20} className="text-terrecuite-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <a
                              href={doc.document_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-gray-900 hover:text-terrecuite-600 block truncate"
                              title={doc.document_url.split('/').pop()}
                            >
                              {doc.document_url.split('/').pop()}
                            </a>
                            <div className="flex justify-between items-center mt-1">
                              <p className="text-xs text-gray-500">
                                {doc.utilisateur_id ? (
                                  <span className="inline-flex items-center">
                                    <User size={12} className="mr-1" />
                                    Utilisateur #{doc.utilisateur_id}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center">
                                    <Cpu size={12} className="mr-1" />
                                    Automatique
                                  </span>
                                )}
                              </p>
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-terrecuite-100 text-terrecuite-800">
                                {doc.document_url.split('.').pop().toUpperCase()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardBody>
            )}
          </Card>
          </div>
          
          {/* Colonne droite - Contacts et activité */}
          <div className="space-y-6">
            {/* Dates */}
            <Card className="border-terrecuite-200">
              <CardHeader 
                title="Dates" 
                icon={Calendar} 
                collapsible 
                isOpen={openSections.dates}
                onToggle={() => toggleSection('dates')}
                className="bg-terrecuite-50 text-terrecuite-800"
              />
              {openSections.dates && (
                <CardBody>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      label="Création"
                      name="created_at"
                      type="date"
                      value={form.created_at}
                      onChange={handleChange}
                      disabled={true}
                      icon={Calendar}
                      className="border-terrecuite-300"
                    />
                    <FormField
                      label="Deadline client"
                      name="deadline_client"
                      type="date"
                      value={form.deadline_client}
                      onChange={handleChange}
                      error={backendErrors.deadline_client?.[0]}
                      disabled={true}
                      icon={Calendar}
                      className="border-terrecuite-300 focus:ring-terrecuite-500"
                    />
                  </div>
                </CardBody>
              )}
            </Card>
          {form.type === 'bug' && (
        <Card className="border-terrecuite-200 mt-6">
          <CardHeader 
            title="Capture du bug" 
            icon={ImageIcon} 
            collapsible 
            isOpen={openSections.bugImage}
            onToggle={() => toggleSection('bugImage')}
            className="bg-terrecuite-50 text-terrecuite-800"
          />
          {openSections.bugImage && (
            <CardBody>
              {form.bug_image_url ? (
                <div className="flex flex-col items-center">
                  <img 
                    src={form.bug_image_url} 
                    alt="Capture du bug" 
                    className="max-w-full h-auto rounded-lg border border-gray-200"
                  />
                  <a 
                    href={form.bug_image_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="mt-2 text-sm text-terrecuite-600 hover:text-terrecuite-800 flex items-center"
                  >
                    <ExternalLink size={14} className="mr-1" />
                    Voir en grand
                  </a>
                </div>
              ) : (
                <div className="text-center py-4">
                  <ImageOff size={24} className="mx-auto text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">Aucune image de bug disponible</p>
                </div>
              )}
            </CardBody>
          )}
        </Card>
          )}
            
            {/* Détails demande (conditionnel) */}
            {form.type === 'demande' && (
              <Card className="border-terrecuite-200">
                <CardHeader 
                  title="Détails demande" 
                  icon={Clock} 
                  collapsible 
                  isOpen={openSections.demande}
                  onToggle={() => toggleSection('demande')}
                  className="bg-terrecuite-50 text-terrecuite-800"
                />
                {openSections.demande && (
                  <CardBody>
                    <FormField
                      label="Jours validés"
                      name="nbr_jrs_valider"
                      type="number"
                      value={form.nbr_jrs_valider}
                      onChange={handleChange}
                      error={backendErrors.nbr_jrs_valider?.[0]}
                      disabled={true}
                      icon={Clock}
                      className="border-terrecuite-300"
                    />
                  </CardBody>
                )}
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}